import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import SeoImage from "@/components/SeoImage";
import { dentistJsonLd, breadcrumbJsonLd } from "@/lib/schema";
import { useI18n } from "@/lib/i18n";

export default function Blog() {
  const { t, lang } = useI18n();
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)); }, []);

  return (
    <>
      <SEOHead
        title="Blog · Katia Falón Odontología"
        description="Artículos sobre salud oral, tratamientos y bienestar dental, escritos por Katia Falón Odontología en Armenia, Quindío."
        path="/blog"
        jsonLd={[dentistJsonLd(), breadcrumbJsonLd([{name:"Inicio",path:"/"},{name:"Blog",path:"/blog"}])]}
      />
      <section data-testid="blog-page" className="mx-auto max-w-6xl px-6 py-16 lg:px-10 lg:py-24">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">{t("blog.eyebrow")}</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl">{t("blog.title")}</h1>
          <p className="mt-4 text-[hsl(var(--clinic-muted))]">
            {t("blog.body")}
          </p>
        </div>
        <div className="mt-12 grid gap-8 md:grid-cols-2">
          {posts.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-[hsl(var(--clinic-border))] p-10 text-center text-sm text-[hsl(var(--clinic-muted))]">
              {t("blog.empty")}
            </div>
          )}
          {posts.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group flex flex-col overflow-hidden rounded-2xl border border-[hsl(var(--clinic-border))] bg-white transition-transform hover:-translate-y-1 hover:shadow-lg"
            >
              {p.cover_slug ? (
                <div className="aspect-[16/9] w-full overflow-hidden bg-[hsl(var(--clinic-bg))]">
                  <SeoImage
                    slug={p.cover_slug}
                    alt={p.cover_alt || p.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    testId={`blog-cover-${p.slug}`}
                  />
                </div>
              ) : (
                <div className="aspect-[16/9] w-full bg-gradient-to-br from-[hsl(var(--clinic-primary))]/20 to-[hsl(var(--clinic-secondary))]/10" />
              )}
              <div className="flex flex-1 flex-col p-6">
                <div className="text-xs uppercase tracking-widest text-[hsl(var(--clinic-muted))]">
                  {new Date(p.created_at).toLocaleDateString(lang === "en" ? "en-US" : "es-CO", { year: "numeric", month: "long", day: "numeric" })} · {t("blog.readingTime")}
                </div>
                <h2 className="mt-2 font-display text-2xl">{p.title}</h2>
                <p className="mt-3 flex-1 text-sm text-[hsl(var(--clinic-muted))]">{p.excerpt}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(p.tags || []).slice(0, 4).map((tg) => (
                    <span key={tg} className="rounded-full bg-[hsl(var(--clinic-bg))] px-2.5 py-0.5 text-xs text-[hsl(var(--clinic-muted))]">
                      {tg}
                    </span>
                  ))}
                </div>
                <div className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--clinic-secondary))]">
                  {t("common.readMore")} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
